import React from 'react';

import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CircularProgress, Box } from '@mui/material';
import { clearSessionPreservingTab } from '../utils/tabSession';
import { isCompanyChoicePending } from '../tenancy/companySession';
import { PICKER_PATH, COMPANIES_PATH } from '../tenancy/companies';

// Pages every signed-in person may open, whatever their role's route list says.
// Companies & routing was split out of Settings (/userProfile), which every
// role has, but the backend's per-role route lists were never given the new
// path — so it answered Unauthorized to everyone. What each person sees on it
// is still decided by the page itself.
const ALWAYS_ALLOWED_PATHS = [COMPANIES_PATH];

export default function Protected({ children, role }) {
  const authUser = sessionStorage.getItem('token');
  const location = useLocation();
  const currentPath = location.pathname;
  const userDetails = useSelector((state) => state?.userInfo?.userInfo);
  const isRehydrated = useSelector((state) => state?._persist?.rehydrated);
  const reduxCognitoUser = useSelector(
    (state) => state?.signIn?.signIn?.cognitoRes?.accessToken?.payload?.username
  );
  const sessionCognitoUser = sessionStorage.getItem('cognitoUsername');

  const rolesConfig = userDetails?.roleConfigurations?.authConfiguration;
  if (!authUser) {
    return <Navigate to="/" state={{ from: history.location }} />;
  }

  // Signed in, but with several companies and none chosen yet: nothing can
  // load until they choose (tenancy/). Never set for a person with one company.
  if (isCompanyChoicePending()) {
    return <Navigate to={PICKER_PATH} state={{ from: location }} />;
  }

  // Last line of defence: the rehydrated store must belong to the token in this
  // tab. If they disagree the state came from a different account — throw it
  // away and re-authenticate rather than render one company's data under
  // another company's token.
  if (reduxCognitoUser && sessionCognitoUser && reduxCognitoUser !== sessionCognitoUser) {
    clearSessionPreservingTab();
    return <Navigate to="/" state={{ from: location }} />;
  }

  // Still rehydrating redux-persist AND no user details yet — show a loader
  if (!isRehydrated && !userDetails) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}>
        <CircularProgress />
      </Box>
    );
  }

  // If rolesConfig is not configured (e.g. Admin role), allow access
  if (!rolesConfig) {
    return children;
  }

  if (ALWAYS_ALLOWED_PATHS.includes(currentPath)) {
    return children;
  }

  const allowedRoutes = rolesConfig[role] || rolesConfig['defaultRole'];
  const pathMatches = allowedRoutes?.some((route) => {
    const regexPattern = route?.replace(/\/:id$/, '(?:/[a-zA-Z0-9_-]+)?')?.replace(/\//g, '\\/');

    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(currentPath);
  });

  if (!pathMatches) {
    return <Navigate to="/unauthorized" state={{ from: location }} />;
  }

  return children;
}
